import { QUEUED_SAMPLE_ACTION } from '@/enums';
import React, { createContext, useCallback, useReducer } from 'react';

export type DnaSampleSelectedType = {
  value: string;
  label: string;
  batchName?: string;
};
interface ISampleContext {
  queuedSamples: DnaSampleSelectedType[];
  handleAddSamples?: (payload: DnaSampleSelectedType[]) => void;
  handleClearSamples?: () => void;
}
interface SampleState {
  queueSamples: DnaSampleSelectedType[];
}
interface ActionReducer {
  type: QUEUED_SAMPLE_ACTION;
  payload: DnaSampleSelectedType[];
}

const defaultValuesContext: ISampleContext = {
  queuedSamples: [],
};
export const SampleContext =
  createContext<ISampleContext>(defaultValuesContext);

const initialQueued: SampleState = {
  queueSamples: [],
};
export const queuedReducer = (
  state: SampleState = initialQueued,
  action: ActionReducer,
) => {
  const handleCreate = (state: SampleState, action: ActionReducer) => {
    if (action.type) {
      switch (action.type) {
        case QUEUED_SAMPLE_ACTION.ADD_TO_QUEUE:
          const sampleIdsAdded = action.payload;

          const newQueueSamples = sampleIdsAdded.reduce(
            (ac: DnaSampleSelectedType[], sampleIdAdded) => {
              const isExisted = ac.find(z => z.value === sampleIdAdded.value);

              if (!isExisted) {
                ac.push(sampleIdAdded);
              }

              return ac;
            },
            [...state.queueSamples],
          );

          return { ...state, queueSamples: newQueueSamples };

        default:
          return state;
      }
    }
    return state;
  };

  const handleDelete = (state: SampleState, action: ActionReducer) => {
    switch (action.type) {
      case QUEUED_SAMPLE_ACTION.DELETE_FROM_QUEUE:
        const samples = action.payload as DnaSampleSelectedType[];
        const sampleIdsDeleted = samples.map(s => s.value);
        const newSampleIds = state.queueSamples.filter(
          sampleId => !sampleIdsDeleted.includes(sampleId.value),
        );
        return { ...state, queueSamples: newSampleIds };

      case QUEUED_SAMPLE_ACTION.CLEAR_QUEUE:
        return { ...state, queueSamples: [] };

      default:
        return state;
    }
  };

  switch (action.type) {
    case QUEUED_SAMPLE_ACTION.ADD_TO_QUEUE:
      return handleCreate(state, action);

    case QUEUED_SAMPLE_ACTION.DELETE_FROM_QUEUE:
    case QUEUED_SAMPLE_ACTION.CLEAR_QUEUE:
      return handleDelete(state, action);

    default:
      return state;
  }
};

export const SampleProvider = (props: { children: React.ReactNode }) => {
  const { children } = props;
  const [queuedSamplesState, dispatch] = useReducer(
    queuedReducer,
    initialQueued,
  );

  const handleAddSamples = useCallback(
    (payload: DnaSampleSelectedType[]) =>
      dispatch({
        type: QUEUED_SAMPLE_ACTION.ADD_TO_QUEUE,
        payload,
      }),
    [],
  );

  const handleClearSamples = useCallback(
    () =>
      dispatch({
        type: QUEUED_SAMPLE_ACTION.CLEAR_QUEUE,
        payload: initialQueued.queueSamples,
      }),
    [],
  );

  return (
    <SampleContext.Provider
      value={{
        queuedSamples: queuedSamplesState.queueSamples,
        handleAddSamples,
        handleClearSamples,
      }}
    >
      {children}
    </SampleContext.Provider>
  );
};
